import { CONFIG_CONSTRAINTS, IConfigConstraints, IStairConfig, defaultTurnAtStep } from '@stair-craft/common/util';

import { calculateLayout as calculateLShapeLayout } from './calculators/l-shape-calculate-layout/calculate-layout';
import { calculateLayout as calculateUShapeLayout } from './calculators/u-shape-calculate-layout/calculate-layout';
import { RESIDENTIAL_NORMS } from './norms';

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

/**
 * Spiral coupling: the stair diameter and the (square) opening drive each other.
 * A diameter edit sizes the opening to diameter+margin; an opening edit clamps it to
 * the room and shrinks the diameter to keep the margin. No-op off spiral. Extracted
 * from `normalize` to keep its complexity low.
 */
function normalizeSpiralOpening(
    m: IStairConfig,
    prev: IStairConfig,
    sp: IConfigConstraints['spiral'],
    spiralDiameterChanged: boolean,
    openingWidthChanged: boolean,
    openingLengthChanged: boolean
): IStairConfig {
    if (m.type !== 'spiral') {
        return m;
    }
    if (spiralDiameterChanged) {
        // Stair diameter changed → opening = diameter + margin.
        const diameter: number = m.spiralDiameter ?? sp.defaultDiameter;
        const size: number = diameter + sp.openingMargin;
        return {
            ...m,
            spiralDiameter: diameter,
            openingWidth: size,
            openingLength: size,
        };
    }
    if (openingWidthChanged || openingLengthChanged) {
        // Opening changed → clamp it to what the room allows, then shrink the
        // stair diameter to keep the margin.
        const minOpening: number = sp.diameter.min + sp.openingMargin;
        const maxAvailable: number = Math.min(m.roomWidth - m.openingX, m.roomLength - m.openingZ);
        const clamped: number = clamp(m.openingWidth, minOpening, maxAvailable);
        const currentStairDiameter: number = prev.spiralDiameter ?? sp.defaultDiameter;
        const maxStairForOpening: number = clamped - sp.openingMargin;
        const newStairDiameter: number = maxStairForOpening < currentStairDiameter ? maxStairForOpening : currentStairDiameter;
        return { ...m, spiralDiameter: newStairDiameter, openingWidth: clamped, openingLength: clamped };
    }
    return m;
}

/**
 * Grow the opening to fit the stair — only when the opening was not edited directly
 * (a deliberate opening shrink clamps the stair width instead). Extracted from `normalize`.
 */
function growOpeningToStair(m: IStairConfig, openingEdited: boolean): IStairConfig {
    if (openingEdited) {
        return m;
    }
    const requiredWidth: number = m.type === 'u-shape' ? 2 * m.stairWidth : m.stairWidth;
    if (requiredWidth > m.openingWidth) {
        const maxWidth: number = m.roomWidth - m.openingX;
        return { ...m, openingWidth: Math.min(requiredWidth, maxWidth) };
    }
    return m;
}

/**
 * Clamp the opening to the room (length/width caps), shrink the stair to fit the
 * opening, and cap the U-shape flight gap. Extracted from `normalize`.
 */
function clampOpeningToRoom(m: IStairConfig, cn: IConfigConstraints): IStairConfig {
    let result: IStairConfig = m;
    const maxOpeningLength: number = Math.min(cn.opening.length.max, result.roomLength);
    if (result.openingLength > maxOpeningLength) {
        result = { ...result, openingLength: maxOpeningLength };
    }
    const maxOpeningWidth: number = Math.min(cn.opening.width.max, result.roomWidth);
    if (result.openingWidth > maxOpeningWidth) {
        result = { ...result, openingWidth: maxOpeningWidth };
    }
    if (result.stairWidth > result.openingWidth) {
        result = { ...result, stairWidth: result.openingWidth };
    }
    if (result.type === 'u-shape') {
        const maxFlightGap: number = Math.max(0, result.openingWidth - 2 * result.stairWidth);
        if (result.flightGap > maxFlightGap) {
            result = { ...result, flightGap: maxFlightGap };
        }
    }
    return result;
}

/**
 * Opening anchor: when the opening length or the room length changes, keep whichever
 * edge is closer pinned to its wall (derived from `prev`). Spiral openings are square
 * and never anchored. Extracted from `normalize`.
 */
function anchorOpening(m: IStairConfig, prev: IStairConfig, openingLengthChanged: boolean, roomLengthChanged: boolean): IStairConfig {
    if (m.type === 'spiral' || !(openingLengthChanged || roomLengthChanged)) {
        return m;
    }
    const currentZ: number = prev.openingZ;
    const currentDisplayY: number = prev.roomLength - prev.openingZ - prev.openingLength;
    const anchorBackEdge: boolean = currentZ <= currentDisplayY;
    const newZ: number = anchorBackEdge
        ? Math.min(currentZ, Math.max(0, m.roomLength - m.openingLength))
        : Math.max(0, m.roomLength - m.openingLength - currentDisplayY);
    return { ...m, openingZ: newZ };
}

/**
 * Final opening-position clamps to keep it inside the room. Extracted from `normalize`.
 */
function clampOpeningPosition(m: IStairConfig): IStairConfig {
    const maxZ: number = Math.max(0, m.roomLength - m.openingLength);
    const maxX: number = Math.max(0, m.roomWidth - m.openingWidth);
    const clampedZ: number = clamp(m.openingZ, 0, maxZ);
    const clampedX: number = clamp(m.openingX, 0, maxX);
    if (clampedZ !== m.openingZ || clampedX !== m.openingX) {
        return { ...m, openingZ: clampedZ, openingX: clampedX };
    }
    return m;
}

/**
 * The turn step the auto layout picks for this config — the flight 1 count of the joint
 * in-norm split with nothing pinned. Null off L/U. It is the in-norm turn step for the
 * room, floor and opening at hand, which a fixed per-type number is only at the defaults.
 */
export function autoTurnAtStep(config: IStairConfig): number | null {
    if (config.type === 'l-shape') {
        return calculateLShapeLayout({ ...config, turnAtStep: null }, RESIDENTIAL_NORMS).flight1Count;
    }
    if (config.type === 'u-shape') {
        return calculateUShapeLayout({ ...config, turnAtStep: null }, RESIDENTIAL_NORMS).flight1Count;
    }
    return null;
}

/** A turn step nobody moved: still the type's default for its turn type, or the auto pick. */
function isUntouchedTurnStep(config: IStairConfig): boolean {
    return (
        config.turnAtStep != null &&
        (config.turnAtStep === defaultTurnAtStep(config.type, config.turnType) || config.turnAtStep === autoTurnAtStep(config))
    );
}

/**
 * An untouched L/U turn step follows the auto layout through every edit — turn type,
 * floor height, room, opening, angle. A fixed turn step is in norm only for the room it
 * was chosen for: winders take two (L) or five (U) more risers than a landing, and the
 * landing's step 9 put the winder L-shape at 19 × 158 mm, Blondel 582; a floor height
 * off the default did the same to a landing. A turn step the user moved stays put.
 */
function followAutoTurnStep(m: IStairConfig, prev: IStairConfig): IStairConfig {
    if (m.type !== prev.type || m.turnAtStep !== prev.turnAtStep || !isUntouchedTurnStep(prev)) {
        return m;
    }
    const turnAtStep: number | null = autoTurnAtStep(m);
    return turnAtStep === m.turnAtStep ? m : { ...m, turnAtStep };
}

/**
 * Config normalization — repair a candidate stair config so it satisfies the
 * cross-field geometric invariants: the opening fits the room, the stair fits
 * the opening, U-shape flights fit the opening, and the opening stays anchored
 * to a wall. Pure and DI-free: the single point every config writer (panel
 * edit, viewport opening drag, JSON import) funnels through behind the session,
 * so they all settle identically.
 *
 * `prev` is the config before the edit; the change is inferred by diffing it
 * against `next`, which carries the writer's raw intent. The opening anchor is
 * derived from `prev` each pass — no stored state.
 */
export function normalize(prev: IStairConfig, next: IStairConfig): IStairConfig {
    const cn: IConfigConstraints = CONFIG_CONSTRAINTS;

    const openingLengthChanged: boolean = next.openingLength !== prev.openingLength;
    const openingWidthChanged: boolean = next.openingWidth !== prev.openingWidth;
    const roomLengthChanged: boolean = next.roomLength !== prev.roomLength;
    const spiralDiameterChanged: boolean = next.spiralDiameter !== prev.spiralDiameter;
    const openingEdited: boolean = openingLengthChanged || openingWidthChanged;

    // Each step repairs one invariant, in order; a config writer's intent settles
    // deterministically through the same pipeline. Steps are no-ops when their
    // precondition doesn't hold (wrong stair type, opening not edited, already in bounds).
    let m: IStairConfig = { ...next };
    m = normalizeSpiralOpening(m, prev, cn.spiral, spiralDiameterChanged, openingWidthChanged, openingLengthChanged);
    m = growOpeningToStair(m, openingEdited);
    m = clampOpeningToRoom(m, cn);
    m = anchorOpening(m, prev, openingLengthChanged, roomLengthChanged);
    m = clampOpeningPosition(m);
    m = followAutoTurnStep(m, prev);

    return m;
}
